import { FormikErrors } from 'formik'
import { SaleInvoiceComplianceExtraFieldsFieldFragment } from 'generated/resources/graphql'

import { FormErrors } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import {
  FormComplianceExtraFields,
  LineItem,
  SaleInvoiceFormFields,
} from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/SaleInvoiceForm/types'

export const validateLineItems = (lineItems: LineItem[]): FormikErrors<LineItem>[] => {
  const lineItemsErrors: FormikErrors<LineItem>[] = lineItems.map((item) => {
    const itemErrors: FormikErrors<LineItem> = {}

    if (!item.description) {
      itemErrors.description = i18n.t('finance.sale_invoices.form.error_required')
    }
    if (!item.quantity || item.quantity == 0) {
      itemErrors.quantity = i18n.t('finance.sale_invoices.form.error_one_or_higher')
    }
    if (!item.amount || item.amount < 0) {
      itemErrors.amount = i18n.t('finance.sale_invoices.form.error_required')
    }
    if (!item.taxRate) {
      itemErrors.taxRate = i18n.t('finance.sale_invoices.form.error_required')
    }
    return itemErrors
  })

  const hasErrors = lineItemsErrors.some((lineItemsError) => Object.keys(lineItemsError).length > 0)
  return hasErrors ? lineItemsErrors.filter((item) => Object.keys(item).length > 0) : []
}

export const validateComplianceExtraFields = (
  complianceExtraFields: FormComplianceExtraFields | null | undefined,
  isCreditNote?: boolean
): FormikErrors<SaleInvoiceComplianceExtraFieldsFieldFragment>[] => {
  if (!complianceExtraFields) {
    return []
  }

  const complianceExtraFieldsErrors: FormikErrors<SaleInvoiceComplianceExtraFieldsFieldFragment>[] =
    complianceExtraFields.fields.map((item) => {
      const itemErrors: FormikErrors<SaleInvoiceComplianceExtraFieldsFieldFragment> = {}

      if (
        !item.value &&
        (isCreditNote ? item.mandatoryForCreditNote : item.mandatoryForStandardInvoice)
      ) {
        itemErrors.name = i18n.t('finance.sale_invoices.form.error_required')
      }
      return itemErrors
    })

  const hasErrors = complianceExtraFieldsErrors.every(
    (fieldError) => Object.keys(fieldError).length == 0
  )
  return hasErrors ? [] : complianceExtraFieldsErrors.filter((item) => Object.keys(item).length > 0)
}

export const validateSaleInvoiceForm = (
  formData: SaleInvoiceFormFields
): FormErrors<SaleInvoiceFormFields> => {
  const errors: FormErrors<SaleInvoiceFormFields> = {}

  if (formData.action == 'saving') {
    return errors
  }

  if (!formData.salesInvoiceSequence) {
    errors.salesInvoiceSequence = i18n.t('finance.sale_invoices.form.error_required')
  }

  if (!formData.operationDate) {
    errors.operationDate = i18n.t('finance.sale_invoices.form.error_required')
  }

  if (!formData.dueDate) {
    errors.dueDate = i18n.t('finance.sale_invoices.form.error_required')
  }

  if (!formData.legalEntity) {
    errors.legalEntity = i18n.t('finance.sale_invoices.form.error_required')
  }

  if (!formData.contact) {
    errors.contact = i18n.t('finance.sale_invoices.form.error_required')
  }

  if (!formData.currency) {
    errors.currency = i18n.t('finance.sale_invoices.form.error_required')
  }

  if (formData.dueDate && formData.operationDate && formData.dueDate < formData.operationDate) {
    errors.dueDate = i18n.t('finance.sale_invoices.form.due_date_invalid')
  }

  if (
    formData.discountPercentage &&
    (formData.discountPercentage < 0 || formData.discountPercentage > 100)
  ) {
    errors.discountPercentage = i18n.t('finance.sale_invoices.form.discount_less_than_or_equal_100')
  }

  if (formData.lineItems.length == 0) {
    errors.lineItems = i18n.t('finance.sale_invoices.form.error_required')
  }

  if (formData.currency !== formData.legalEntity?.currency && !formData.exchangeRate) {
    errors.exchangeRate = i18n.t('finance.sale_invoices.form.error_required')
  }

  const lineItemsErrors = validateLineItems(formData.lineItems)
  const complianceExtraFieldErrors = validateComplianceExtraFields(
    formData.complianceExtraFields,
    formData.documentType === 'credit_note'
  )

  if (lineItemsErrors.length > 0) {
    errors.lineItems = lineItemsErrors
  }
  if (complianceExtraFieldErrors.length > 0) {
    errors.complianceExtraFields = complianceExtraFieldErrors.map((err) => err.name).join(', ')
  }

  return errors
}

export const isValidForIssuing = (
  formValues: SaleInvoiceFormFields,
  validateLineItemsFn: (lineItems: LineItem[]) => FormikErrors<LineItem>[],
  validateComplianceExtraFieldsFn: (
    complianceExtraFields: FormComplianceExtraFields | null | undefined,
    isCreditNote?: boolean
  ) => FormikErrors<SaleInvoiceComplianceExtraFieldsFieldFragment>[]
): boolean => {
  return (
    !!formValues.currency &&
    !!formValues.salesInvoiceSequence &&
    !!formValues.operationDate &&
    !!formValues.dueDate &&
    !!formValues.legalEntity &&
    !!formValues.contact &&
    formValues.lineItems.length > 0 &&
    validateLineItemsFn(formValues.lineItems).length === 0 &&
    validateComplianceExtraFieldsFn(
      formValues.complianceExtraFields,
      formValues.documentType === 'credit_note'
    ).length === 0 &&
    (formValues.currency === formValues.legalEntity?.currency || !!formValues.exchangeRate)
  )
}
